
export interface IShareTestimonialForm {
  firstName: string;
  email?: string;
  city: string;
  public?: boolean;
  lang: string;
  text?: string;
  audio?: any;
  audioUrl?: any;
  xCsrfToken?: string;
  makeAnonymous?: boolean;
}

export interface IShareTestimonialPanels {
  content: string;
}

export type TestimonialSteps =
  | "intro"
  | "choice"
  | "voice"
  | "voiceQuit"
  | "text"
  | "sign"
  | "confirmation";

import {translate} from "@/helpers/locale-helper";
import {ValidatorManager} from "@/helpers/validator";
import {
  DataStore,
  EventStore,
  MenuStore,
  ModalStore,
  ToastStore,
} from "@/store";
import {Options, Vue} from "vue-class-component";
import BusEvent from "../../../../shared/js/BusEvent";
import SoundWave from "@/components/SoundWave/SoundWave.vue";
import isEmail from "isemail";

@Options({
  components: {
    SoundWave,
  },
  props: {
    id: Number,
    show: Boolean,
    submitAnimation: Boolean,
    shareTestimony: Boolean,
    openWithBusEvent: Boolean,
    showExit: Boolean,
  },
  emits: [
    "opened",
    "toggled",
    "recording-status",
    "share-finish",
    "step",
    "close",
  ],
})
export default class MemorialShareTestimony extends Vue {
  declare $refs: {
    modal: HTMLElement;
    recordBtn: HTMLElement;
    panelsText: HTMLElement;
    textareaText: HTMLTextAreaElement;
    textareaBars: HTMLElement;
    makeAnonymous: HTMLElement;
    modalTestimony: HTMLElement;
    uploadProgress: HTMLElement;
    soundwave: typeof SoundWave;
  };

  id!: number;
  modalUnwatch!: Function;
  openWithBusEventUnwatch!: Function;
  show!: boolean;
  escapeKey: boolean = true;
  display: boolean = false;
  submitAnimation: boolean = false;
  openWithBusEvent: boolean = false;
  timers: number[] = [];

  recordingInterval?: number;
  recordingElapsedInterval?: number;
  circular: boolean = false;

  // Panels auto change
  panelIndex = 0;
  nextPanelTimer = 0;
  autoPanelDelay = 7000; // 5 seconds.
  panelHeight: number = 0;
  visiblePanels: any;
  activeTimeout!: number;
  maxCharacters?: number;

  step: TestimonialSteps = "intro";
  recording: boolean = false;
  message: string = "";
  audioChunks: Blob[] = [];
  audioUrl: string | null = null;

  stream: MediaStream | null = null;
  mediaRecorder?: MediaRecorder;
  readonly maxRecording: number = 90;
  recordingRemain: number = -1;
  firstNameCache: string = "";

  form: IShareTestimonialForm = {
    firstName: "",
    email: undefined,
    city: "",
    text: undefined,
    lang : "",
    public: undefined,
    audio: undefined,
    audioUrl: undefined,
    xCsrfToken: undefined,
    makeAnonymous: undefined
  };
  unwatchClose!: Function;
  unwatchStep!: Function;
  finishExperience: boolean = false;
  isUploading: boolean = false;
  wordsCountMax: number = 1500;
  anonymous: boolean = false;
  manager: ValidatorManager = new ValidatorManager([
    (context) => {
      if (isEmail.validate(this.form.email ?? "") === false) {
        context.addError("email", {
          fr: "Veuillez saisir une adresse e-mail",
          en: "Please enter a email address",
        });
      }
    },
  ]);

  get lang ()
  {
    return this.$i18n.locale;
  }

  get emailError() {
    return this.manager.getErrorSummary("email", this.$i18n.locale);
  }

  get publicError() {
    return this.manager.getErrorSummary("public", this.$i18n.locale);
  }

  get panels(): IShareTestimonialPanels[] {
    let headingsContent = [];
    // reworked a bit because when the prompts were empty, they would still appear
    // but it just looked like a missing heading
    for (let i = 1; i < 4; i++) {
      const translated = translate(
        this.$i18n.locale,
        "testimonial.prompt_" + i
      );

      if (translated && translated.trim().length > 0) {
        headingsContent.push({
          content: translated,
        });
      }
    }

    return headingsContent;
  }

  get remainingThemes() {
    return DataStore.remainingThemes;
  }

  get startRecordingTitle() {
    return translate(this.$i18n.locale, "testimonial.record_start");
  }

  get firstName() {
    return translate(this.$i18n.locale, "form.first_name");
  }

  get city() {
    return translate(this.$i18n.locale, "form.city");
  }

  get email() {
    return translate(this.$i18n.locale, "form.email");
  }

  get returnToExperience() {
    return translate(this.$i18n.locale, "testimonial.return_to_experience");
  }

  get sendComplete() {
    return translate(this.$i18n.locale, "testimonial.send_complete");
  }

  get btnCloseTitle() {
    return translate(this.$i18n.locale, "general.modal_close");
  }

  observeHeight() {
    this.visiblePanels = document.querySelector(".panels");

    if (this.visiblePanels) {
      setTimeout(() => {
        document.querySelectorAll(".panel").forEach((el) => {
          if (el.scrollHeight > this.panelHeight) {
            this.panelHeight = el.scrollHeight;
          }
        });
      }, 150);
    }

    this.activeTimeout = setTimeout(this.observeHeight.bind(this), 50);
  }

  startNextPanelTimer(): void {
    this.nextPanelTimer = setInterval(() => {
      if (this.panelIndex !== this.panels.length - 1) {
        this.panelIndex++;
      } else if (this.panelIndex == this.panels.length - 1) {
        this.panelIndex = 0;
      }
    }, this.autoPanelDelay);
  }

  requestVoiceAccess() {
    this.playSoundClick();
    if (this.stream) {
      return;
    }

    this.prepareMediaRecorder();
  }

  prepareMediaRecorder() {
    navigator.mediaDevices
      .getUserMedia({audio: true})
      .then((stream) => {
        this.stream = stream;

        this.$refs.soundwave.prepareReactiveSoundWave(stream, false);
        this.$refs.soundwave.drawLayers();

        if (this.stream) {
          this.mediaRecorder = new MediaRecorder(this.stream);

          this.mediaRecorder.addEventListener("dataavailable", (event) => {
            this.audioChunks.push(event.data);
          });

          this.mediaRecorder.addEventListener("stop", () => {
            this.$refs.recordBtn.classList.remove("active");
            this.form.audio = new Blob(this.audioChunks);
            this.audioUrl = URL.createObjectURL(this.form.audio);

            this.recording = false;
            this.$emit("recording-status", false);
            this.clearInterval();
          });
        }

        if (this.step == "intro") {
          this.voice();
        }
      })
      .catch((error: Error) => {
        if (error && error.message == "Permission denied") {
          // permission denied let the client know with a toast or something
          ToastStore.sendMessage({
            message: this.$t("permissions.voice_permission_denied"),
            shake: true,
          });
          return;
        }

        ToastStore.sendMessage({
          message: this.$t("permissions.voice_unsupported"),
          shake: true,
        });
      });
  }

  choice() {
    this.step = "choice";
    this.playSoundClick();
  }

  voice() {
    this.step = "voice";
    this.panelIndex = 0;
    this.startNextPanelTimer();
    this.observeHeight();
    this.playSoundClick();
  }

  resetRecord() {
    this.audioUrl = null;
    this.recordingRemain = this.maxRecording;

    if (this.$refs.soundwave) {
      this.$refs.soundwave.removeContext();
    }

    this.prepareMediaRecorder();
  }

  text() {
    this.step = "text";
    this.panelIndex = 0;
    this.startNextPanelTimer();
    this.observeHeight();
    this.playSoundClick();
  }

  playSoundClick() {
    const eventUI = new BusEvent(BusEvent.EVENTS.PLAY_SOUND, {
      key: "VM_D_1_",
      loop: false,
      group: "UI",
    });
    document.dispatchEvent(eventUI);
  }

  notNow() {
    this.playSoundClick();
    this.close();
  }

  addClass() {
    this.doActionWhenRefReady(() => {
      const modal = this.$refs.modal;
      // modal.classList.add(`paper-model-${Math.floor(Math.random() * 3)}`);

      // 2022-03-30 requested to always use papier 2
      modal.classList.add('paper-model-1');
    });
  }

  open() {
    this.display = true;

    this.$emit("opened");
    this.$emit("toggled", this.display);
    MenuStore.close();

    this.addClass();
  }

  close(): void {
    if (!this.finishExperience && this.step != "voiceQuit" && this.audioChunks.length > 0) {
      this.step = "voiceQuit";
      return;
    }

    if (this.$refs.soundwave) {
      this.$refs.soundwave.destroy();
    }

    this.playSoundClick();
    document.dispatchEvent(
      new BusEvent(BusEvent.EVENTS.SHARE_TESTIMONIAL_CLOSED)
    );
    this.stopRecording();
    this.$emit("close");

    ModalStore.onClose();

    const modal = this.$refs.modal;
    if (
      this.submitAnimation &&
      !modal.classList.contains("share-submit-animation")
    ) {
      modal.classList.add("share-submit-animation");
    }

    this.display = false;

    for (let timer of this.timers) {
      clearTimeout(timer);
    }

    this.$emit("toggled", this.display);
  }

  isFormValid() {
    return this.manager.validate();
  }

  finish() {
    this.$emit("share-finish");

    EventStore.triggerFinishExperience();
    DataStore.setFinishExperience(true);
    this.finishExperience = true;

    this.close();


  }

  sign() {
    this.step = "sign";
    this.playSoundClick();
  }

  get remainingTime() {
    if (this.recordingRemain >= 0) {
      return this.recordingRemain.toString();
    }

    return "";
  }

  async submit() {
    //console.log("submit");


    //let bodyForm = JSON.stringify(this.form);

    this.playSoundClick();


    this.manager.validate();
    if (!this.manager.isValid) {
      return;
    }

    let meta = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']");
    let csrf = meta?.content ?? "";
    //eval ('this.form["x-csrf-token"] = csrf'); //Typescript makes me hate MS so much!!!
    this.isUploading = true;
    if (this.audioUrl) {
      let strFilename = Date.now() + ".mp3";
      let file = new File([this.form.audio], strFilename, {
        type: "audio/mp3",
      });
      /*
       let chunk = 0;
       let start = 0;
       let chunkSize = 100000;

       let end = Math.min(start + chunkSize, file.size);
       let bReachEnd = false

       */

      //while (!bReachEnd)
      {
        //const chunk = file.slice(start, end);

        const form = new FormData();

        form.append("file", this.form.audio, strFilename);
        //form.append("filename", strFilename);

        //bReachEnd = end >= file.size; //it should always be equal, but let now take any chance

        //TODO: Maybe we will need chunking, or maybe not
        let response = await fetch("/endpoints/memorial/upload-chunks", {
          method: "POST",
          cache: "no-cache",
          headers: {
            "X-CSRF-TOKEN": csrf,
          },
          body: form,
        });

        // start = end;
        //  end = Math.min(start + chunkSize, file.size);

        let json = await response.json();
        //console.log("UPLOAD response", response);

        this.form.audioUrl = json.url;

      }

      //eval("window[\"__BUS__\"].upload(file);") //TYPESCRIPT FTW!
    }

    let bodyForm = JSON.stringify(this.form);
    let fff = this.form;

   // console.log("FORM");
    //console.log(bodyForm);
    //console.log(fff);

    // forcing a key:value rather than just not sending it
    if (!this.form.public) {
      this.form.public = false;
    }

    if (!this.anonymous) {
      this.form.makeAnonymous = false;
    }


    let response = await fetch("/endpoints/memorial/save-testimonial", {
      method: "POST",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrf,
      },
      body: JSON.stringify(this.form),
    });

    this.isUploading = false;
    this.step = "confirmation";


  }

  playRecording() {
    const audio = new Audio(this.audioUrl as string);
    audio.play();
  }

  toggleRecord() {
    this.playSoundClick();

    if (!this.recording) {
      this.form.audio = undefined;
      this.audioUrl = null;
      this.audioChunks = [];
      this.recording = true;
      this.$refs.recordBtn.classList.add("active");

      if (this.mediaRecorder) {
        this.mediaRecorder.start();
      }

      this.$refs.soundwave.setCanDraw(true);

      this.circular = false;

      this.recordingRemain = this.maxRecording;

      this.recordingElapsedInterval = setInterval(() => {
        this.recordingRemain--;

        if (this.recordingRemain === 0) {
          this.stopRecording();
        }
      }, 1 * 1000);

      this.$emit("recording-status", true);
      return;
    }

    this.$refs.soundwave.setCanDraw(false);

    // request to stop, the stop event will be catch to complete the audio file
    this.stopRecording();
  }

  stopRecording() {
    clearInterval(this.recordingElapsedInterval);

    if (this.mediaRecorder && this.mediaRecorder.state != "inactive") {
      this.mediaRecorder.stop();
    }

    if (this.stream) {
      this.stream?.getAudioTracks().forEach((track) => {
        track.stop();
      });
    }
  }

  textareaBarDisapear() {
    this.maxCharacters = 25;

    if (this.$refs.textareaText.value.length >= this.maxCharacters) {
      this.$refs.textareaBars.classList.add("hide");
    } else {
      this.$refs.textareaBars.classList.remove("hide");
    }
  }

  makeAnonymous() {
    this.anonymous = !this.anonymous;

    if (this.anonymous) {
      this.firstNameCache = this.form.firstName;
      this.form.firstName = "";
    } else {
      this.form.firstName = this.firstNameCache;
    }
  }

  clearInterval() {
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
    }

    if (this.recordingElapsedInterval) {
      clearInterval(this.recordingElapsedInterval);
    }

    this.circular = false;
  }

  get textLength() {
    if (!this.form.text) {
      return this.wordsCountMax;
    }
    return this.wordsCountMax - this.form.text.length;
  }

  reset(full: boolean = false) {
    this.step = "intro";
    this.audioChunks = [];
    this.stream = null;
    this.form.audio = undefined;
    this.form.audioUrl = undefined;
    this.mediaRecorder = undefined;
    this.recording = false;
    this.clearInterval();
    this.audioUrl = null;
    this.recordingRemain = this.maxRecording;

    if (full) {
      for (let field of Object.keys(this.form)) {
        (this.form as any)[field] = "";
      }
    }
  }

  mounted() {


    this.form.lang = this.lang;

    this.unwatchStep = this.$watch("step", (value: TestimonialSteps) => {
      this.$emit("step", value);
    });

    this.unwatchClose = ModalStore.$watch(
      (t) => t.onClosed,
      (value: number, _: number) => {
        this.stopRecording();
      }
    );

    // trigger on next frame
    setTimeout(() => {
      this.$emit("step", "intro");
    });

    this.$watch("show", (value: boolean, oldValue: boolean) => {
      //console.log("show =>", value);
      if (value == oldValue) {
        return;
      }

      this.reset();

      if (!value) {
        if (typeof oldValue !== "undefined") {
          this.close();
        }
        return;
      }
      this.open();
    });

    this.openWithBusEventUnwatch = this.$watch(
      "openWithBusEvent",
      (value: boolean) => {
        if (value) {
          this.doActionWhenRefReady(() => {
            this.$refs.modal.classList.add("anim-bus-event");
          });

          return;
        }

        this.doActionWhenRefReady(() => {
          this.$refs.modal.classList.remove("anim-bus-event");
        });
      }
    );

    this.modalUnwatch = ModalStore.$watch(
      (store) => store.closes,
      (value, legacyValue) => {
        /**
         * Cheese solution, the binding of certain data haven't been update before the close event
         */
        this.doActionWhenRefReady(this.close.bind(this));
      }
    );
  }

  unmounted() {
    if (this.openWithBusEventUnwatch) {
      this.openWithBusEventUnwatch();
    }
    if (this.modalUnwatch) {
      this.modalUnwatch();
    }

    for (let timer of this.timers) {
      clearTimeout(timer);
    }

    if (this.unwatchStep) {
      this.unwatchStep();
    }

    if (this.unwatchClose) {
      this.unwatchClose();
    }

    this.clearInterval();
  }

  doActionWhenRefReady(callback: Function) {
    if (this.$refs.modal) {
      callback.bind(this)();
      return;
    }

    this.timers.push(
      setTimeout(this.doActionWhenRefReady.bind(this), 50, callback)
    );
  }
}
